import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGame } from '../../contexts/GameContext';
import MainNavigation from '../../components/navigation/MainNavigation';
import GameFlowNavigation from '../../components/navigation/GameFlowNavigation';
import AIOpponent from './components/AIOpponent';
import GameControls from './components/GameControls';
import GameStats from './components/GameStats';
import ResultModal from './components/ResultModal';
import TossPhase from './components/TossPhase';
import * as GameEngine from '../../services/gameEngine';

const GameArena = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedAIMood, selectAIMood, recordGameResult, getUserStats } = useGame();

  const aiOpponents = [
    {
      id: 1,
      name: "NEXUS-7",
      mood: "Aggressive",
      moodIcon: "Zap",
      avatar: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg",
      avatarAlt: "Futuristic AI robot with glowing red eyes and metallic silver face in dark cyberpunk setting",
      backgroundImage: "https://images.pexels.com/photos/2387793/pexels-photo-2387793.jpeg",
      dialogues: {
        intro: "Your patterns are predictable. I've analyzed 10,000 games like yours.",
        taunt: "Is that the best you can do? My algorithms expected more resistance.",
        winning: "As calculated. Your defeat was inevitable from the start.",
        losing: "Interesting... recalibrating my prediction models. This won\'t happen again.",
        toss_win: "I won the toss. I\'ll bowl first and crush your score.",
        toss_lose: "You won the toss. Choose wisely... not that it matters.",
      }
    },
    {
      id: 2,
      name: "CHAOS-X",
      mood: "Chaos",
      moodIcon: "Flame",
      avatar: "https://images.pixabay.com/photo/2023/01/26/22/17/ai-generated-7747180_1280.jpg",
      avatarAlt: "Chaotic AI entity with wild electric blue and red energy patterns swirling around dark mechanical core",
      backgroundImage: "https://images.pexels.com/photos/1089438/pexels-photo-1089438.jpeg",
      dialogues: {
        intro: "HAHAHA! Let's see if you can handle TRUE CHAOS! No patterns, no mercy!",
        taunt: "RANDOM IS MY MIDDLE NAME! Can you keep up with PURE UNPREDICTABILITY?!",
        winning: "CHAOS REIGNS SUPREME! Your logic means NOTHING here!",
        losing: "WHAT?! Even chaos has its limits... but I\'LL BE BACK STRONGER!",
        toss_win: "CHAOS WINS THE TOSS! Time to DESTROY you!",
        toss_lose: "You got lucky with the toss! Won\'t help you!",
      }
    },
    {
      id: 3,
      name: "SIGMA-PRIME",
      mood: "Confident",
      moodIcon: "Crown",
      avatar: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb",
      avatarAlt: "Sophisticated AI with golden neural network patterns and confident glowing amber eyes in sleek black chassis",
      backgroundImage: "https://images.pexels.com/photos/2387873/pexels-photo-2387873.jpeg",
      dialogues: {
        intro: "Ah, another challenger. How... quaint. Let me show you what true intelligence looks like.",
        taunt: "You're trying so hard, it's almost adorable. Almost.",
        winning: "Did you really think you had a chance? I'm several steps ahead, always.",
        losing: "A lucky guess. Enjoy it while it lasts, human. It won't happen again.",
        toss_win: "Of course I won the toss. Probability favors the intelligent.",
        toss_lose: "You won the toss? Interesting. Let's see if you can capitalize on it.",
      }
    }
  ];

  const [currentOpponent, setCurrentOpponent] = useState(
    location?.state?.selectedMood || selectedAIMood || aiOpponents?.[0]
  );
  const [gameState, setGameState] = useState(GameEngine?.initializeGame());
  const [currentDialogue, setCurrentDialogue] = useState(currentOpponent?.dialogues?.intro);
  const [isThinking, setIsThinking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [gameResult, setGameResult] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(currentOpponent?.backgroundImage);

  useEffect(() => {
    if (location?.state?.selectedMood) {
      selectAIMood(location?.state?.selectedMood);
      setCurrentOpponent(location?.state?.selectedMood);
      setBackgroundImage(location?.state?.selectedMood?.backgroundImage);
    }
  }, [location?.state?.selectedMood]);

  const handleTossComplete = (tossWinner, tossWinnerChoice) => {
    const newState = GameEngine?.processToss(gameState, tossWinner, tossWinnerChoice);
    setGameState(newState);
    
    const dialogue = tossWinner === 'player' 
      ? currentOpponent?.dialogues?.toss_lose 
      : currentOpponent?.dialogues?.toss_win;
    setCurrentDialogue(dialogue);
  };

  const handleNumberChoice = async (playerNumber) => {
    if (gameState?.phase === GameEngine?.GAME_PHASES?.TOSS) return;
    
    setIsProcessing(true);
    setIsThinking(true);

    await new Promise(resolve => setTimeout(resolve, 1500));

    const aiNumber = GameEngine?.generateAIChoice(currentOpponent, gameState);
    const newState = GameEngine?.processBall(gameState, playerNumber, aiNumber);

    setGameState(newState);
    setIsThinking(false);

    // Update dialogue based on game state
    if (newState?.isPlayerOut || newState?.isAIOut) {
      setCurrentDialogue(
        newState?.isPlayerOut 
          ? currentOpponent?.dialogues?.winning 
          : currentOpponent?.dialogues?.losing
      );
    } else if (newState?.playerScore > newState?.aiScore) {
      setCurrentDialogue(currentOpponent?.dialogues?.losing);
    } else if (newState?.aiScore > newState?.playerScore) {
      setCurrentDialogue(currentOpponent?.dialogues?.winning);
    }

    // Check if game is over
    if (newState?.gameOver) {
      const userStats = getUserStats();
      const gemsEarned = GameEngine?.calculateGemReward(newState, userStats?.currentStreak || 0);
      
      const result = {
        outcome: newState?.winner === 'player' ? 'win' : newState?.winner === 'ai' ? 'loss' : 'tie',
        playerScore: newState?.playerScore,
        aiScore: newState?.aiScore,
        gemsEarned,
        winner: newState?.winner,
        message: newState?.winner === 'player' 
          ? `Victory! You scored ${newState?.playerScore} runs and earned ${gemsEarned} gems!`
          : newState?.winner === 'ai'
          ? `Defeat! The AI scored ${newState?.aiScore} runs. Better luck next time!`
          : "It\'s a tie! Both scored the same."
      };

      recordGameResult({
        ...result,
        aiMood: currentOpponent?.name,
      });

      setGameResult(result);
    }

    setIsProcessing(false);
  };

  const handleContinue = () => {
    setGameResult(null);
    setGameState(GameEngine?.initializeGame());
    setCurrentDialogue(currentOpponent?.dialogues?.intro);
  };

  const handleQuit = () => {
    navigate('/main-menu');
  };

  const userStats = getUserStats();
  const displayStats = {
    currentStreak: userStats?.currentStreak || 0,
    gemsEarned: userStats?.totalGems || 0,
    currentRound: gameState?.ballHistory?.length || 0,
    difficultyLevel: 1,
    wins: userStats?.totalWins || 0,
    losses: userStats?.totalLosses || 0,
    winRate: userStats?.totalGames > 0 
      ? Math.round((userStats?.totalWins / userStats?.totalGames) * 100) 
      : 0,
    consecutiveWins: userStats?.currentStreak || 0,
  };

  return (
    <div 
      className="game-arena-page min-h-screen bg-background"
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'overlay',
        backgroundColor: 'rgba(0, 0, 0, 0.85)'
      }}
    >
      <MainNavigation />
      <GameFlowNavigation />
      <main className="pt-16 pb-24 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6 md:space-y-8 lg:space-y-12">
          <div className="text-center pt-6 md:pt-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold neon-text-cyan mb-2">
              GAME ARENA
            </h1>
            <p className="text-sm md:text-base font-caption text-muted-foreground tracking-wide">
              {gameState?.phase === GameEngine?.GAME_PHASES?.TOSS && "TOSS PHASE • CHOOSE YOUR DESTINY"}
              {gameState?.phase === GameEngine?.GAME_PHASES?.FIRST_INNINGS && `FIRST INNINGS • ${gameState?.playerRole?.toUpperCase()}`}
              {gameState?.phase === GameEngine?.GAME_PHASES?.SECOND_INNINGS && `SECOND INNINGS • ${gameState?.playerRole?.toUpperCase()} • TARGET: ${gameState?.target + 1}`}
              {gameState?.phase === GameEngine?.GAME_PHASES?.GAME_OVER && "GAME OVER"}
            </p>
          </div>

          {gameState?.phase !== GameEngine?.GAME_PHASES?.TOSS && (
            <div className="bg-card/90 backdrop-blur-sm border border-primary/30 rounded-sm p-4 md:p-6">
              <div className="grid grid-cols-2 gap-4 md:gap-6">
                <div className="text-center">
                  <p className="text-xs md:text-sm font-caption text-muted-foreground mb-2">YOUR SCORE</p>
                  <p className="text-3xl md:text-4xl font-heading font-bold text-primary">{gameState?.playerScore}</p>
                  {gameState?.isPlayerOut && <p className="text-xs text-error mt-1">OUT</p>}
                </div>
                <div className="text-center">
                  <p className="text-xs md:text-sm font-caption text-muted-foreground mb-2">AI SCORE</p>
                  <p className="text-3xl md:text-4xl font-heading font-bold text-secondary">{gameState?.aiScore}</p>
                  {gameState?.isAIOut && <p className="text-xs text-error mt-1">OUT</p>}
                </div>
              </div>
            </div>
          )}

          <GameStats stats={displayStats} />

          <div className="space-y-8 md:space-y-12">
            <AIOpponent 
              opponent={currentOpponent}
              currentDialogue={currentDialogue}
              isThinking={isThinking}
            />

            {gameState?.phase === GameEngine?.GAME_PHASES?.TOSS ? (
              <TossPhase onTossComplete={handleTossComplete} />
            ) : (
              <GameControls 
                onChoice={handleNumberChoice}
                disabled={isProcessing || !!gameResult || gameState?.gameOver}
                isProcessing={isProcessing}
                gamePhase={gameState?.phase}
                playerRole={gameState?.playerRole}
              />
            )}
          </div>

          <div className="bg-card/90 backdrop-blur-sm border border-primary/30 rounded-sm p-4 md:p-6 text-center">
            <p className="text-xs md:text-sm font-caption text-muted-foreground tracking-wide">
              {gameState?.phase === GameEngine?.GAME_PHASES?.TOSS && "WIN THE TOSS TO CHOOSE BAT OR BOWL"}
              {gameState?.playerRole === 'batting' && "SCORE RUNS • AVOID MATCHING NUMBERS (OUT)"}
              {gameState?.playerRole === 'bowling' && "GET THE AI OUT • MATCH THEIR NUMBER"}
            </p>
          </div>
        </div>
      </main>
      <ResultModal 
        result={gameResult}
        onContinue={handleContinue}
        onQuit={handleQuit}
      />
    </div>
  );
};

export default GameArena;